import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, 
  HelpCircle, 
  LogOut, 
  XCircle,
  CheckCircle2,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { authService } from '../services/auth';
import { subscriptionService } from '../services/subscription';
import { toast } from '../hooks/use-toast';

const Settings = () => {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [cancellationDate, setCancellationDate] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Cargar estado de suscripción al montar el componente
  useEffect(() => {
    const loadSubscriptionStatus = async () => {
      try {
        setLoadingStatus(true);
        const statusResponse = await subscriptionService.getStatus();
        
        if (statusResponse?.success && statusResponse?.data) {
          setSubscriptionStatus(statusResponse.data);
          
          // Si ya está cancelada, formatear la fecha de cancelación
          if (statusResponse.data.cancel_at_period_end && statusResponse.data.current_period_end) {
            try {
              const endDateObject = new Date(statusResponse.data.current_period_end);
              // Verificar que la fecha sea válida
              if (!isNaN(endDateObject.getTime())) {
                const dateStr = endDateObject.toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });
                const timeStr = endDateObject.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                setCancellationDate(`${dateStr}, a las ${timeStr}`);
              } else {
                console.warn('Fecha inválida recibida del backend:', statusResponse.data.current_period_end);
              }
            } catch (dateError) {
              console.warn('Error formateando fecha de cancelación:', dateError, 'Fecha recibida:', statusResponse.data.current_period_end);
            }
          }
        }
      } catch (error) {
        console.error('Error cargando estado de suscripción:', error);
        // Si no tiene suscripción, no hacer nada
      } finally {
        setLoadingStatus(false);
      }
    };

    loadSubscriptionStatus();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);

      // Llamar al servicio real de cancelación
      const result = await subscriptionService.cancelSubscription();
      // result esperado: { success, message, data: { subscription_id, status, cancel_at_period_end, current_period_end } }

      if (result?.success) {
        // Formatear fecha de finalización si viene
        let endDateFormatted = null;
        let endDateObject = null;
        if (result?.data?.current_period_end) {
          try {
            endDateObject = new Date(result.data.current_period_end);
            // Formato: "15 de enero de 2025, a las 23:59"
            const dateStr = endDateObject.toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
            const timeStr = endDateObject.toLocaleTimeString('es-ES', {
              hour: '2-digit',
              minute: '2-digit',
            });
            endDateFormatted = `${dateStr}, a las ${timeStr}`;
          } catch (dateError) {
            console.warn('Error formateando fecha:', dateError);
          }
        }

        // Guardar la fecha para mostrarla en el diálogo de éxito
        setCancellationDate(endDateFormatted);
        
        // Actualizar el estado de la suscripción
        setSubscriptionStatus({
          ...subscriptionStatus,
          cancel_at_period_end: result.data.cancel_at_period_end,
          current_period_end: result.data.current_period_end,
        });
        
        // Cerrar el diálogo de confirmación
        setShowCancelDialog(false);
        
        // Mostrar el diálogo de éxito con la fecha
        setShowSuccessDialog(true);
      } else {
        toast({
          title: "No se pudo cancelar la suscripción",
          description: result?.error || result?.message || "Inténtalo de nuevo más tarde.",
          variant: "destructive",
        });
        setShowCancelDialog(false);
      }
    } catch (error) {
      console.error("Error cancelando suscripción:", error);

      // Intentar obtener mensaje del backend si existe
      const backendMessage =
        error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        "Ocurrió un problema al procesar tu solicitud.";

      toast({
        title: "Error al cancelar suscripción",
        description: backendMessage,
        variant: "destructive",
      });
      setShowCancelDialog(false);
    } finally {
      setCanceling(false);
    }
  };

  // Determinar si la suscripción ya está cancelada
  const isSubscriptionCancelled = subscriptionStatus?.cancel_at_period_end === true;
  
  // Formatear fecha de cancelación para mostrar en el banner
  const formatCancellationDate = (dateString) => {
    if (!dateString) return null;
    try {
      const endDateObject = new Date(dateString);
      // Verificar que la fecha sea válida
      if (isNaN(endDateObject.getTime())) {
        console.warn('Fecha inválida:', dateString);
        return null;
      }
      const dateStr = endDateObject.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const timeStr = endDateObject.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${dateStr}, a las ${timeStr}`;
    } catch (error) {
      console.warn('Error formateando fecha:', error, 'Fecha recibida:', dateString);
      return null;
    }
  };

  const settingsItems = [
    {
      id: 'contact',
      title: 'Contacto',
      description: 'Información de contacto',
      icon: Mail,
      action: () => {
        window.open('mailto:contacto@miembrospremier.com', '_blank');
      },
    },
    {
      id: 'support',
      title: 'Soporte técnico',
      description: 'Obtén ayuda con tu cuenta',
      icon: HelpCircle,
      action: () => {
        window.open('mailto:contacto@miembrospremier.com?subject=Soporte Técnico', '_blank');
      },
    },
    {
      id: 'cancel',
      title: 'Cancelar mis beneficios',
      description: isSubscriptionCancelled 
        ? (subscriptionStatus?.current_period_end 
            ? `Cancelación programada para el ${formatCancellationDate(subscriptionStatus.current_period_end)}`
            : 'Ya has cancelado tu suscripción')
        : 'Cancelar mi suscripción',
      icon: XCircle,
      action: () => setShowCancelDialog(true),
      variant: 'destructive',
      disabled: isSubscriptionCancelled,
    },
    {
      id: 'logout',
      title: 'Cerrar sesión',
      description: 'Salir de tu cuenta',
      icon: LogOut,
      action: handleLogout,
      variant: 'outline',
    },
  ];

  return (
    <>
      <div className="px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Configuración</h2>
            <p className="text-[#A2A2A2]">Gestiona tu cuenta y preferencias</p>
          </div>

          {/* Alert: Suscripción Cancelada */}
          {!loadingStatus && isSubscriptionCancelled && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <h3 className="text-yellow-400 font-semibold text-lg">
                    Tu suscripción ha sido cancelada
                  </h3>
                  <div className="space-y-2 text-yellow-300/90">
                    {subscriptionStatus?.current_period_end ? (
                      <>
                        <p>
                          Tu suscripción se cancelará automáticamente el{' '}
                          <span className="font-bold text-yellow-200 text-base">
                            {formatCancellationDate(subscriptionStatus.current_period_end)}
                          </span>.
                        </p>
                        <p className="text-sm">
                          Hasta esa fecha, tu suscripción seguirá activa y podrás disfrutar de todos tus beneficios.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm">
                        Tu suscripción será cancelada al final del período actual. Continuarás teniendo acceso hasta entonces.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Settings List */}
          <div className="space-y-3">
            {settingsItems.map((item) => {
              const Icon = item.icon;
              const isDisabled = item.disabled === true;
              
              return (
                <button
                  key={item.id}
                  onClick={isDisabled ? undefined : item.action}
                  disabled={isDisabled}
                  className={`w-full bg-black border border-white/10 rounded-3xl p-6 transition-all text-left ${
                    isDisabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:border-white/30 cursor-pointer hover:scale-[1.02] active:scale-[0.98]'
                  } ${
                    item.variant === 'destructive' && !isDisabled ? 'hover:border-red-500/30' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.variant === 'destructive' 
                        ? isDisabled
                          ? 'bg-gray-500/20 border border-gray-500/30'
                          : 'bg-red-500/20 border border-red-500/30'
                        : item.variant === 'outline'
                        ? 'bg-white/5 border border-white/20'
                        : 'bg-white/10 border border-white/20'
                    }`}>
                      <Icon className={`w-6 h-6 ${
                        item.variant === 'destructive' 
                          ? isDisabled 
                            ? 'text-gray-400' 
                            : 'text-red-400' 
                          : 'text-white'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${
                        item.variant === 'destructive' 
                          ? isDisabled 
                            ? 'text-gray-400' 
                            : 'text-red-400' 
                          : 'text-white'
                      }`}>
                        {item.title}
                      </h3>
                      <p className={`text-sm ${
                        isDisabled ? 'text-gray-500' : 'text-[#A2A2A2]'
                      } ${item.id === 'cancel' && isSubscriptionCancelled && subscriptionStatus?.current_period_end ? 'leading-relaxed' : ''}`}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Cancelar suscripción</DialogTitle>
            <DialogDescription className="text-[#A2A2A2]">
              ¿Estás seguro de que deseas cancelar tu suscripción? Perderás acceso a todos los beneficios 
              una vez que finalice tu período actual.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              className="flex-1 border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full"
              disabled={canceling}
            >
              No, mantener suscripción
            </Button>
            <Button
              onClick={handleCancelSubscription}
              disabled={canceling}
              className="flex-1 bg-red-500 text-white hover:bg-red-600 rounded-full"
            >
              {canceling ? 'Cancelando...' : 'Sí, cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Alert Dialog - Cancelación Exitosa */}
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="bg-black border-white/20 text-white max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <AlertDialogTitle className="text-2xl font-bold text-white">
                ¡Cancelación exitosa!
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-[#A2A2A2] space-y-3 pt-2">
              <p className="text-base">
                Tu suscripción ha sido cancelada exitosamente. Continuarás teniendo acceso a todos los beneficios hasta el final de tu período actual.
              </p>
              
              {cancellationDate && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-blue-400" />
                    <span className="text-white font-semibold">Tu suscripción se cancelará el:</span>
                  </div>
                  <p className="text-blue-400 text-xl font-bold mb-3">
                    {cancellationDate}
                  </p>
                  <p className="text-[#A2A2A2] text-sm">
                    Hasta esa fecha y hora, tu suscripción seguirá activa y podrás seguir disfrutando de todos tus beneficios. La cancelación se aplicará automáticamente a partir de ese momento.
                  </p>
                </div>
              )}
              
              {!cancellationDate && (
                <p className="text-sm text-yellow-400 mt-2">
                  ⚠️ La fecha de cancelación no está disponible, pero tu suscripción será cancelada al final del período actual.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <Button
              onClick={() => {
                setShowSuccessDialog(false);
                // Recargar el estado de la suscripción después de cerrar el diálogo
                const reloadStatus = async () => {
                  try {
                    const statusResponse = await subscriptionService.getStatus();
                    if (statusResponse?.success && statusResponse?.data) {
                      setSubscriptionStatus(statusResponse.data);
                      if (statusResponse.data.cancel_at_period_end && statusResponse.data.current_period_end) {
                        const formattedDate = formatCancellationDate(statusResponse.data.current_period_end);
                        setCancellationDate(formattedDate);
                      }
                    }
                  } catch (error) {
                    console.error('Error recargando estado:', error);
                  }
                };
                reloadStatus();
              }}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full"
            >
              Entendido
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Settings;

